import { consentDocument } from "../consentDocument"
import { ConsentType, ShownConsents, useSignConsent } from "../hooks"
import { Box, Divider, ForwardIcon, Typography, dayjs } from "@carrotfertility/carotene-core"
import { Form, FormAlert, FormButton, FormCheckbox, FormTextField } from "@carrotfertility/carotene-core-x"
import React from "react"
import { BookingBreadcrumbs } from "../Breadcrumbs"

// INTERIM (MEM-756): a functional placeholder so the persistence loop is testable end-to-end in stage.
// The real per-consent pages, copy, and layout are the consent-UI ticket. Flag-gated/stage-only.

// Fixed render order; only the outstanding subset is shown.
const CONSENT_ORDER: ConsentType[] = ["telehealth_informed_consent", "notice_of_privacy_practices", "hipaa"]
// Consents that do NOT gate Continue. HIPAA is shown annually and optionally signed (see the BFF gate).
const OPTIONAL_CONSENTS = new Set<ConsentType>(["hipaa"])

type ConsentFormValues = {
  telehealth_informed_consent: boolean
  notice_of_privacy_practices: boolean
  hipaa: boolean
  // Typed-name signature. Gating only (MEM-755): required to submit, but never sent or stored.
  signature: string
}

type ConsentPageProps = {
  // The consents the member still needs to act on (from getConsentStatus) — reported (with signed
  // true/false) on submit so the BFF records seen/signed.
  outstandingConsents: ConsentType[]
  onContinue: () => void
  onReturnToProviderResults?: () => void
}

// Literal strings (no react-intl): this package isn't in the intl extraction pipeline and the
// experience is English-only for the foreseeable future. All copy comes from consentDocument.
export function ConsentPage({
  outstandingConsents,
  onContinue,
  onReturnToProviderResults
}: ConsentPageProps): JSX.Element {
  const signConsent = useSignConsent()

  const outstanding = new Set(outstandingConsents)
  const shownTypes = CONSENT_ORDER.filter((type) => outstanding.has(type))
  // The typed-name signature ratifies the telehealth informed consent + consent to treat, so it appears
  // only when that consent is outstanding.
  const showSignature = outstanding.has("telehealth_informed_consent")

  // Report what the member did per shown consent: required ones are checked to submit (signed true);
  // HIPAA carries its checkbox state. The BFF maps this to stored seen/signed. The typed name gates the
  // form but is never sent — the session is the "who", and signedAt is stamped server-side.
  const handleSubmit = (values: ConsentFormValues) => {
    const shown: ShownConsents = shownTypes.map((type) => ({ type, signed: Boolean(values[type]) }))
    signConsent.mutate(shown, { onSuccess: onContinue })
  }

  // Display-only "Signed on" date; not the recorded signing time (that's stamped server-side).
  const signedOn = dayjs().format("MMM D, YYYY")

  return (
    <Form<ConsentFormValues>
      onSubmit={handleSubmit}
      defaultValues={{
        telehealth_informed_consent: false,
        notice_of_privacy_practices: false,
        hipaa: false,
        signature: ""
      }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.lg),
          maxInlineSize: 640
        })}
      >
        <BookingBreadcrumbs onReturnToSearchResults={onReturnToProviderResults} />

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <Typography variant="h2" component="h1">
            {consentDocument.title}
          </Typography>
          <Typography>{consentDocument.intro}</Typography>
        </Box>

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.md) })}
        >
          {shownTypes.map((type) => {
            const entry = consentDocument.consents[type]
            const optional = OPTIONAL_CONSENTS.has(type)
            return (
              <Box
                key={type}
                component="section"
                sx={(theme) => ({
                  display: "flex",
                  flexDirection: "column",
                  gap: theme.spacing(theme.tokens.spacing.xs)
                })}
              >
                <Typography variant="h5" component="h2">
                  {entry.section.heading}
                </Typography>
                <Typography>{entry.section.body}</Typography>
                <FormCheckbox
                  name={type}
                  label={entry.clause.label}
                  controllerProps={optional ? {} : { rules: { required: entry.clause.requiredMessage } }}
                />
              </Box>
            )
          })}
        </Box>

        {showSignature && (
          <>
            <Divider />
            <Box
              component="section"
              sx={(theme) => ({
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(theme.tokens.spacing.md)
              })}
            >
              <Typography>{consentDocument.signature.attestation}</Typography>
              <FormTextField
                name="signature"
                label={consentDocument.signature.label}
                helperText={consentDocument.signature.helper}
                registerOptions={{
                  required: consentDocument.signature.requiredMessage,
                  // React Hook Form's `required` treats "   " as filled; the typed name is the
                  // e-signature, so reject whitespace-only.
                  validate: (value: string) => value.trim().length > 0 || consentDocument.signature.requiredMessage
                }}
              />
              <Box sx={(theme) => ({ display: "flex", gap: theme.spacing(theme.tokens.spacing.xxxl) })}>
                <Typography variant="body1" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
                  Signed on
                </Typography>
                <Typography variant="body1">{signedOn}</Typography>
              </Box>
            </Box>
          </>
        )}

        {signConsent.isError && (
          <FormAlert severity="error">Something went wrong saving your consent. Please try again.</FormAlert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <FormButton type="submit" color="secondary" endIcon={<ForwardIcon />}>
            Continue
          </FormButton>
        </Box>
      </Box>
    </Form>
  )
}
